/**
 * ReasonAnalyticsCard — Feb 2026 · Iter94
 *
 * Slim KPI card rendered at the top of the Projects dashboard.
 * Shows win-rate, avg days-to-archive, reason mix + a 12-week
 * trend sparkline sourced from `/api/projects/analytics/90d`.
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChartLineUp, ClockClockwise, Trophy } from "@phosphor-icons/react";
import { getProjectsAnalytics } from "@/lib/api";

const REASON_COLOUR = {
  won:       "#059669",
  lost:      "#e11d48",
  paused:    "#d97706",
  completed: "#475569",
  other:     "#334155",
};

function Sparkline({ trend }) {
  const points = useMemo(() => {
    if (!trend?.length) return { d: "", height: 32, w: 120 };
    const w = 120, h = 32;
    const max = Math.max(1, ...trend.map((t) => t.total || 0));
    const step = w / Math.max(1, trend.length - 1);
    const d = trend.map((t, i) => {
      const x = i * step;
      const y = h - (t.total / max) * (h - 4) - 2;
      return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(" ");
    return { d, width: w, height: h };
  }, [trend]);
  return (
    <svg viewBox={`0 0 ${points.width || 120} ${points.height || 32}`}
             className="w-24 h-8" data-testid="analytics-sparkline">
      <path d={points.d} fill="none" stroke="#1d4ed8" strokeWidth="1.5" />
    </svg>
  );
}

export default function ReasonAnalyticsCard() {
  const q = useQuery({
    queryKey: ["projects-analytics", "90d"],
    queryFn:  () => getProjectsAnalytics(90),
    staleTime: 60_000,
  });
  if (q.isLoading || !q.data) return null;
  const {
    total_archives, win_rate, avg_days_to_archive,
    reason_mix, weekly_trend,
  } = q.data;
  if (!total_archives) return null;  // hide when there's nothing to show

  return (
    <div className="rounded-lg border border-slate-200 bg-gradient-to-b from-white to-slate-50
                     px-4 py-3 flex items-center gap-6 flex-wrap"
             data-testid="reason-analytics-card">
      <div className="flex-1 min-w-[160px]">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">
          Lifecycle · last 90 days
        </div>
        <div className="text-[11px] text-slate-500 mt-0.5">
          {total_archives} archive{total_archives === 1 ? "" : "s"} in the window
        </div>
      </div>

      {/* Win-rate */}
      <div className="text-center" data-testid="analytics-win-rate">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono inline-flex items-center gap-1">
          <Trophy size={10} className="text-emerald-600" weight="fill" /> Win-rate
        </div>
        <div className="text-[22px] font-heading font-black text-emerald-700 leading-none tracking-tight">
          {win_rate != null ? `${win_rate}%` : "—"}
        </div>
      </div>

      {/* Avg days to archive */}
      <div className="text-center" data-testid="analytics-avg-days">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono inline-flex items-center gap-1">
          <ClockClockwise size={10} className="text-slate-600" /> Avg days
        </div>
        <div className="text-[22px] font-heading font-black text-slate-800 leading-none tracking-tight">
          {avg_days_to_archive != null ? avg_days_to_archive : "—"}
        </div>
      </div>

      {/* Reason mix pills */}
      <div className="flex items-center gap-1 flex-wrap" data-testid="analytics-mix">
        {Object.entries(reason_mix || {}).filter(([, n]) => n > 0).map(([k, n]) => (
          <span key={k}
                className="inline-flex items-center gap-1 rounded-full px-2 py-0.5
                               text-[10.5px] font-mono uppercase tracking-widest text-white"
                style={{ background: REASON_COLOUR[k] || "#334155" }}
                data-testid={`analytics-mix-${k}`}>
            {k} · {n}
          </span>
        ))}
      </div>

      {/* Sparkline */}
      <div className="text-center" data-testid="analytics-trend">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono inline-flex items-center gap-1">
          <ChartLineUp size={10} className="text-brand-blue" /> 12-wk trend
        </div>
        <Sparkline trend={weekly_trend} />
      </div>
    </div>
  );
}
